import { z } from 'zod';

import { isoDateTimeSchema, semverSchema, sha256HexSchema, uuidSchema } from '../common/primitives';

/**
 * Das Manifest — der Soll-Zustand eines Servers als Beschreibung.
 *
 * ## Warum es das gibt
 *
 * Bis M4a entstand ein Worker aus einem Dutzend Handgriffen, und die Liste der
 * Edge-Adressen stand an drei Orten fest verdrahtet: in `master.env`, im
 * Firewall-Skript jedes Workers und in der `.env` jeder Edge. Eine zweite Edge
 * hinzuzufügen hieß, drei Dateien auf drei Maschinen anzufassen — und wer die
 * Firewall vergaß, hatte eine Edge, die ihre Worker nicht erreicht. Das sieht
 * aus wie ein defekter Worker und ist eine vergessene Zeile.
 *
 * Das Manifest löst diese Verdrahtung auf: Der Master weiß, welche Edges und
 * welche Worker es gibt, und beschreibt daraus für jeden Server, wie seine
 * Dateien auszusehen haben. Ein Timer holt die Beschreibung ab und stellt sie
 * her.
 *
 * ## Die Regel, die dieses Schema durchsetzt
 *
 * **Ein Manifest enthält niemals eine Zeichenkette, die zu einer Kommandozeile
 * wird.** Es beschreibt Dateiinhalte und nennt Neustart-Ziele aus einer festen
 * Aufzählung — mehr nicht. Was der Empfänger mit `RESTART_TARGETS.agent` tut,
 * steht in seinem eigenen Skript und nicht in der Antwort des Masters.
 *
 * Das ist dieselbe Regel wie bei `siteSpecSchema` gegenüber dem Docker-Socket,
 * und sie ist hier aus demselben Grund nötig: Der Empfänger dieses Dokuments
 * läuft als `root`. Ohne diese Grenze wäre das Manifest eine Fernsteuerung mit
 * Wurzelrechten, und ein Tippfehler im Master würde zu einem Befehl auf zwanzig
 * Servern.
 *
 * ## Warum die Version ein Hash ist
 *
 * `version` ist der SHA-256 über den Inhalt des Manifests, nicht eine Zählnummer
 * und kein Zeitstempel. Damit beantwortet ein Vergleich zweier Zeichenketten die
 * Frage „hat sich etwas geändert?" vollständig — dieselbe Denkweise wie
 * `desiredSpecHash` gegen `observedSpecHash` bei einer Site. Eine Zählnummer
 * müsste jemand erhöhen, und genau das vergisst man bei der Änderung, die es
 * gerade eilig hatte.
 */

/**
 * Ein absoluter Pfad auf dem Zielserver.
 *
 * Bewusst eng: Ein Manifest schreibt Dateien als `root`. Ein relativer Pfad
 * hinge davon ab, in welchem Verzeichnis das Sync-Skript gerade steht, und `..`
 * führte aus jedem gedachten Bereich heraus. Beides wird hier abgelehnt statt
 * im Skript entschärft — eine Prüfung im Vertrag gilt für jeden Empfänger,
 * eine Prüfung im Skript nur für den, der sie hat.
 */
export const absolutePathSchema = z
  .string()
  .min(2, { message: 'Pfad ist zu kurz.' })
  .max(512, { message: 'Pfad darf höchstens 512 Zeichen lang sein.' })
  .regex(/^\/[\w./-]+$/, {
    message:
      'Muss ein absoluter Pfad aus Buchstaben, Ziffern, Punkt, Unterstrich, Bindestrich und Schrägstrich sein.',
  })
  .refine((value) => !value.includes('..'), {
    message: 'Pfad darf kein ".." enthalten.',
  })
  .refine((value) => !value.includes('//'), {
    message: 'Pfad darf keinen doppelten Schrägstrich enthalten.',
  });

/**
 * Dateirechte als viertstellige Oktalzahl.
 *
 * Als Zeichenkette und nicht als Zahl: `0600` als Zahlliteral ist in JSON die
 * Dezimalzahl 600, und die Umrechnung nach Oktal hätte irgendwo stattzufinden.
 * Der Ort, an dem sie vergessen wird, ist immer der, an dem eine Datei mit
 * Zugangsdaten dann `rwxrw----` trägt.
 */
export const fileModeSchema = z.string().regex(/^0[0-7]{3}$/, {
  message: 'Muss eine viertstellige Oktalzahl mit führender Null sein, z. B. "0600".',
});

/**
 * Was nach einer Änderung neu gestartet werden soll.
 *
 * Eine feste Aufzählung, **keine** Kommandozeilen. Der Empfänger übersetzt eine
 * Kennung in seine eigene, fest einprogrammierte Handlung; eine unbekannte
 * Kennung protokolliert er und übergeht sie. Ein Master, der etwas verlangt, das
 * dieser Server nicht kennt, darf nichts Unerwartetes auslösen.
 */
export const RESTART_TARGETS = {
  /** Der Worker-Agent als systemd-Dienst. */
  agent: 'agent',
  /** Die Plattformdienste des Workers (Traefik, MariaDB) aus `infra/worker/`. */
  workerCompose: 'worker-compose',
  /** Die Dienste der Edge aus `infra/edge/`. */
  edgeCompose: 'edge-compose',
  /** Das Firewall-Skript. Wird ohnehin bei jedem Durchgang ausgeführt. */
  firewall: 'firewall',
} as const;

export type RestartTarget = (typeof RESTART_TARGETS)[keyof typeof RESTART_TARGETS];

export const restartTargetSchema = z.enum([
  RESTART_TARGETS.agent,
  RESTART_TARGETS.workerCompose,
  RESTART_TARGETS.edgeCompose,
  RESTART_TARGETS.firewall,
]);

/** Eine Datei, die auf dem Zielserver so aussehen soll, wie hier beschrieben. */
export const manifestFileSchema = z.object({
  path: absolutePathSchema,
  mode: fileModeSchema,
  /**
   * Der vollständige Inhalt.
   *
   * Ganze Datei statt Unterschied: Ein Patch setzt voraus, dass die Ausgangslage
   * bekannt ist, und genau das ist sie auf einem Server, den jemand von Hand
   * angefasst hat, nicht. Der volle Inhalt konvergiert immer.
   */
  content: z.string().max(262_144, { message: 'Dateiinhalt darf höchstens 256 KiB groß sein.' }),
  /** SHA-256 über `content`. Der Empfänger prüft nach dem Schreiben gegen diesen Wert. */
  sha256: sha256HexSchema,
  /**
   * Was neu gestartet werden soll, **wenn genau diese Datei sich geändert hat**.
   *
   * Am einzelnen Eintrag und nicht am Manifest als Ganzem: Der Master weiß
   * nicht, was auf dem Zielserver bereits liegt — das weiß nur der Empfänger.
   * Stünde die Liste oben, müsste bei jeder Änderung irgendeiner Datei alles
   * neu starten, und ein geänderter Firewall-Eintrag risse phpMyAdmin-Sitzungen
   * ab, die damit nichts zu tun haben.
   *
   * `null` heißt: Diese Datei wirkt ohne Neustart. Genau das ist der Grund,
   * warum die Serverliste von phpMyAdmin eine JSON-Datei ist und keine
   * Umgebungsvariable — PHP liest sie bei jeder Anfrage neu, und ein neuer
   * Worker kostet keine angemeldete Kundensitzung.
   */
  restart: restartTargetSchema.nullable(),
});

export type ManifestFile = z.infer<typeof manifestFileSchema>;

/** Das auszuliefernde Agent-Artefakt. */
export const agentArtifactSchema = z.object({
  version: semverSchema,
  /**
   * Vollständige URL zum Tarball beim Master.
   *
   * Absolut und nicht relativ, weil das Sync-Skript sie unverändert an `curl`
   * übergibt und die Basis-URL sonst ein zweites Mal irgendwo stehen müsste.
   */
  url: z.string().url({ message: 'Muss eine vollständige URL sein.' }),
  sha256: sha256HexSchema,
});

export type AgentArtifact = z.infer<typeof agentArtifactSchema>;

const manifestEnvelope = {
  /** SHA-256 über den Inhalt dieses Manifests. Der Empfänger vergleicht ihn mit dem zuletzt angewandten. */
  version: sha256HexSchema,
  generatedAt: isoDateTimeSchema,
  files: z.array(manifestFileSchema).max(64),
} as const;

/** Das Manifest eines Worker-Nodes. */
export const nodeManifestSchema = z.object({
  ...manifestEnvelope,
  nodeId: uuidSchema,
  agent: agentArtifactSchema,
});

export type NodeManifest = z.infer<typeof nodeManifestSchema>;

/**
 * Das Manifest einer Edge.
 *
 * Ohne Artefakt: Die Edge betreibt keinen eigenen Code, sondern ausschließlich
 * fremde Images. Was sich bei ihr ändert, sind Konfigurationsdateien — und die
 * stehen vollständig in `files`.
 */
export const edgeManifestSchema = z.object({
  ...manifestEnvelope,
  edgeId: uuidSchema,
});

export type EdgeManifest = z.infer<typeof edgeManifestSchema>;
